import {Injectable} from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpErrorResponse,
} from "@angular/common/http";
import {BehaviorSubject, EMPTY, throwError} from "rxjs";
import {take, catchError, filter, switchMap, retry} from "rxjs/operators";

import {AuthenticationService} from "../_services/authentication.service";
import {IAccount} from "../_models/account/IAccount";
import HttpStatusCode from "../_enums/HttpStatusCode";
import {GuestService} from "../_services/guest.service";
import {isJson} from "./utils";

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private account: IAccount;

  constructor(private authenticationService: AuthenticationService,
              private guestService: GuestService
  ) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler) {
    this.account = this.authenticationService.getAccount();
    const token = this.authenticationService.getToken();
    const guestToken = this.guestService.getToken();

    // daca nu exista token in cookie, atunci nu vom altera requestul
    if (!this.account && !token && !guestToken) {
      return next.handle(request);
    }

    /**
     * adaugam headerul de autentificare
     * daca se cere JWT nou, atunci nu mai adaugam headerul de autentificare
     */
    if (localStorage.getItem("isTokenRefreshing") !== "1") {
      request = this.addToken(request, token);
    }

    // daca avem guestJWT, atunci adaugam in header GuestToken
    if (request.url.includes("guest") && guestToken) {
      request = this.addGuestToken(request, guestToken);
      return next.handle(request).pipe(
        // retry(1),
        catchError((error) => {
            if (error.error.data?.isGuest) {
              if (
                error instanceof HttpErrorResponse &&
                error.status === HttpStatusCode.UNAUTHORIZED &&
                error.error.data.isGuest
              ) {
                this.guestService.logout(error.error.message);
                return EMPTY;
              }
            }
          }
        ));
    }

    return next.handle(request).pipe(
      // retry(1),
      catchError((error) => {
        // se cere JWT nou, insa refresh tokenul a expirat
        if (
          error instanceof HttpErrorResponse &&
          error.status === HttpStatusCode.FORBIDDEN &&
          error.url.includes("refresh-token")
        ) {
          localStorage.setItem("sessionExpiredMessage", "Din motive de securitate ati fost deconectat. Va rugam sa va reconectati.");
          this.authenticationService.clientLogout(true);
          return next.handle(request);
        }

        if (
          error instanceof HttpErrorResponse &&
          error.status === HttpStatusCode.UNAUTHORIZED
        ) {
          // JWT expirat si fara cerere de refresh token? (delogare fortata - cont dezactivat, rol sters; JWT invalid)
          if (!error.error.data?.withRefresh) {
            let message = error.error.message;
            if (error.error.message.toLowerCase().includes('invalid refresh token')) {
              message = "Sesiunea a expirat. Va rugam sa va autentificati din nou.";
            }
            localStorage.setItem("sessionExpiredMessage", message);
            this.authenticationService.clientLogout(true);
            return next.handle(request);
          }
        }

        /**
         * JWT expirat? daca da, vom cere un nou JWT pe baza refreshToken-ului
         * (se pot aduce informatii despre account, drept urmare refresh tokenul este activ)
         */
        if (
          error instanceof HttpErrorResponse &&
          error.status === HttpStatusCode.UNAUTHORIZED &&
          error.error.data.withRefresh &&
          this.account
        ) {
          return this.handle401Error(request, next);
        } else {
          /**
           * JWT expirat si refresh token expirat;
           * se incearca aducerea de informatii despre account, insa se primeste jwt expirat, iar account este null
           */
          if (!this.account && error.error.data?.withRefresh) {
            localStorage.setItem("sessionExpiredMessage", "Sesiunea a expirat. Va rugam sa va autentificati din nou.");
            this.authenticationService.clientLogout(false);
            return EMPTY;
          }

          // handling erori
          let errorMessage = "";
          if (error.error instanceof ErrorEvent) {
            // client
            errorMessage = `Client error: ${error.error.message}`;
          } else {
            // server
            // verificam daca eroarea este JSON
            if (isJson(error.error)) {
              error.error = JSON.parse(error.error);
            }

            if (error.error.errors) {
              const errors = error.error.errors;

              if (typeof errors === "object") {
                const errorsList = [];
                for (const prop in errors) {
                  if (errors.hasOwnProperty(prop)) {
                    errorsList.push(errors[prop]);
                  }
                }

                errorMessage = errorsList.join("<br />");
              } else {
                errorMessage = errors;
              }
            } else {
              errorMessage = error.error.message;
            }
          }

          return throwError(errorMessage);
        }
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: "Bearer " + token,
      },
    });
  }

  private addGuestToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        GuestToken: token
      },
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (
      localStorage.getItem("isTokenRefreshing") === null ||
      localStorage.getItem("isTokenRefreshing") === "0"
    ) {
      localStorage.setItem("isTokenRefreshing", "1");
      this.refreshTokenSubject.next(null);

      if (this.account?.refreshToken && this.account.refreshToken !== "") {
        return this.authenticationService
          .getRefreshToken(this.account.refreshToken)
          .pipe(
            switchMap((token: any) => {
              localStorage.setItem("isTokenRefreshing", "0");
              this.refreshTokenSubject.next(token);

              return next.handle(this.addToken(request, token));
            })
          );
      }
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((jwt) => {
          return next.handle(this.addToken(request, jwt));
        })
      );
    }
  }
}
